import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, Eye, ThumbsUp, MessageSquare, Share, MousePointer,
  ExternalLink, Clock, CheckCircle, AlertCircle, ChevronDown,
} from 'lucide-react';
import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { useProject } from '../../../contexts/ProjectContext';
import { StatCard } from '../../../components/shared';
import { DataTable, DataTableToolbar, useDataTablePrefs } from '../../../components/shared/DataTable';
import {
  Card,
  Skeleton, EmptyState,
} from '../../../components/ui';
import { StatusPill } from '@archieai/ui';
import {
  contentApi,
  STATUS_LABELS,
  CONTENT_TYPE_LABELS,
  SCHEDULE_STATUS_LABELS,
  SCHEDULE_TYPE_LABELS,
  type ContentItem,
  type ContentSchedule,
  type ItemAnalyticsDataPoint,
  type ContentStatus,
} from '../../../api/content';
import { DashboardPageLayout } from '../../../components/layout';

const STATUS_PILL_MAP: Record<ContentStatus, 'neutral' | 'warning' | 'info' | 'success' | 'error'> = {
  planned: 'neutral',
  drafting: 'warning',
  design_ready: 'neutral',
  in_review: 'info',
  final_edits: 'warning',
  ready_to_publish: 'success',
  published: 'success',
  archived: 'neutral',
};

function ItemAnalyticsRow({ workspaceId, itemId }: { workspaceId: string; itemId: string }) {
  const [data, setData] = useState<ItemAnalyticsDataPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    contentApi.getItemAnalytics(workspaceId, itemId, 30)
      .then(setData)
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [workspaceId, itemId]);

  const metrics = data.reduce<Record<string, number>>((acc, dp) => {
    acc[dp.metric_type] = (acc[dp.metric_type] || 0) + dp.total;
    return acc;
  }, {});

  if (loading) {
    return (
      <tr>
        <td colSpan={6} className="px-4 py-3 bg-neutral-50/50">
          <div className="flex items-center gap-6 ml-6">
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} variant="text" width={48} height={12} />)}
          </div>
        </td>
      </tr>
    );
  }

  const metricItems = [
    { key: 'views', label: 'Views', icon: <Eye size={11} /> },
    { key: 'likes', label: 'Likes', icon: <ThumbsUp size={11} /> },
    { key: 'comments', label: 'Comments', icon: <MessageSquare size={11} /> },
    { key: 'shares', label: 'Shares', icon: <Share size={11} /> },
    { key: 'clicks', label: 'Clicks', icon: <MousePointer size={11} /> },
  ];

  const hasData = Object.values(metrics).some(v => v > 0);

  return (
    <tr>
      <td colSpan={6} className="px-4 py-2.5 bg-neutral-50/50">
        {hasData ? (
          <div className="flex items-center gap-6 ml-6">
            {metricItems.map(m => (
              <div key={m.key} className="flex items-center gap-1.5 text-[11px] text-neutral-500">
                {m.icon}
                <span className="font-medium text-neutral-700">{(metrics[m.key] || 0).toLocaleString()}</span>
                <span>{m.label}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="ml-6 text-[11px] text-neutral-400">No analytics data for this item</div>
        )}
      </td>
    </tr>
  );
}

// =============================================================================
// Column config for content overview
// =============================================================================

const ALL_COLUMNS = [
  { id: 'title', label: 'Title' },
  { id: 'status', label: 'Status' },
  { id: 'content_type', label: 'Type' },
  { id: 'author', label: 'Author' },
  { id: 'published', label: 'Published' },
  { id: 'url', label: 'URL' },
];

const DEFAULT_VISIBLE = ['title', 'status', 'content_type', 'author', 'published', 'url'];

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const { currentProject, buildProjectUrl } = useProject();
  const workspaceId = currentProject?.workspaceId;
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [items, setItems] = useState<ContentItem[]>([]);
  const [queue, setQueue] = useState<ContentSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const { prefs, toggleColumn, setDensity, setSorting } = useDataTablePrefs('analytics', DEFAULT_VISIBLE);

  useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    Promise.all([
      contentApi.getAnalyticsSummary(workspaceId, 30),
      contentApi.listItems(workspaceId),
      contentApi.getAllSchedules(workspaceId).catch(() => contentApi.getDistributionQueue(workspaceId)),
    ])
      .then(([summary, items, queue]) => {
        setSummary(summary);
        setItems(items);
        setQueue(queue);
      })
      .catch(() => {
        setSummary({});
        setItems([]);
        setQueue([]);
      })
      .finally(() => setLoading(false));
  }, [workspaceId]);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const dateA = a.actual_publish_date || a.planned_publish_date || '';
      const dateB = b.actual_publish_date || b.planned_publish_date || '';
      return dateB.localeCompare(dateA);
    });
  }, [items]);

  const recentActivity = useMemo(() => {
    return queue
      .filter(s => s.status === 'completed' || s.status === 'failed')
      .sort((a, b) => (b.executed_at || b.scheduled_at).localeCompare(a.executed_at || a.scheduled_at))
      .slice(0, 10);
  }, [queue]);

  const hasAnyMetrics = Object.values(summary).some(v => v > 0);

  const columns = useMemo<ColumnDef<ContentItem, any>[]>(() => [
    {
      id: 'title',
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => (
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={e => {
              e.stopPropagation();
              setExpandedItems(prev => {
                const next = new Set(prev);
                if (next.has(row.original.id)) next.delete(row.original.id);
                else next.add(row.original.id);
                return next;
              });
            }}
            className="text-neutral-400 hover:text-neutral-600 shrink-0"
          >
            <ChevronDown size={14} className={`transition-transform ${expandedItems.has(row.original.id) ? '' : '-rotate-90'}`} />
          </button>
          <button
            onClick={e => { e.stopPropagation(); navigate(buildProjectUrl(`co/${row.original.id}`, 'sm')); }}
            className="text-sm font-medium text-neutral-800 truncate hover:text-coral-600 transition-colors"
          >
            {row.original.title}
          </button>
        </div>
      ),
      enableSorting: true,
      size: 300,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <StatusPill
          variant={STATUS_PILL_MAP[getValue() as ContentStatus]}
          label={STATUS_LABELS[getValue() as ContentStatus]}
        />
      ),
      enableSorting: true,
    },
    {
      id: 'content_type',
      accessorKey: 'content_type',
      header: 'Type',
      cell: ({ getValue }) => (
        <span className="text-xs text-neutral-500">{CONTENT_TYPE_LABELS[getValue() as ContentItem['content_type']]}</span>
      ),
      enableSorting: true,
    },
    {
      id: 'author',
      accessorKey: 'author_name',
      header: 'Author',
      cell: ({ getValue }) => (
        <span className="text-xs text-neutral-500 truncate block">{(getValue() as string) || '\u2014'}</span>
      ),
      enableSorting: true,
    },
    {
      id: 'published',
      accessorFn: row => row.actual_publish_date || row.planned_publish_date || '',
      header: 'Published',
      cell: ({ row }) => {
        const dateStr = row.original.actual_publish_date || row.original.planned_publish_date;
        return (
          <span className="text-xs text-neutral-500">
            {dateStr
              ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
              : '\u2014'}
          </span>
        );
      },
      enableSorting: true,
    },
    {
      id: 'url',
      accessorKey: 'published_url',
      header: 'URL',
      cell: ({ getValue }) => {
        const url = getValue() as string | undefined;
        return url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-coral-500 hover:text-coral-600"
            onClick={e => e.stopPropagation()}
          >
            <ExternalLink size={10} /> View
          </a>
        ) : (
          <span className="text-xs text-neutral-300">{'\u2014'}</span>
        );
      },
      enableSorting: false,
    },
  ], [expandedItems, navigate, buildProjectUrl]);

  const columnVisibility = useMemo<VisibilityState>(() => {
    const vis: VisibilityState = {};
    ALL_COLUMNS.forEach(c => { vis[c.id] = prefs.visibleColumns.includes(c.id); });
    return vis;
  }, [prefs.visibleColumns]);

  return (
    <DashboardPageLayout
      title="Analytics"
      subtitle="Content performance and engagement metrics (last 30 days)"
      icon={<BarChart3 size={20} />}
    >
      <div className="space-y-8">
        {/* Metric Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard icon={<Eye size={20} />} value={loading ? '\u2014' : (summary['total_views'] || 0).toLocaleString()} label="Total Views" color="blue" />
          <StatCard icon={<ThumbsUp size={20} />} value={loading ? '\u2014' : (summary['total_likes'] || 0).toLocaleString()} label="Total Likes" color="coral" />
          <StatCard icon={<MessageSquare size={20} />} value={loading ? '\u2014' : (summary['total_comments'] || 0).toLocaleString()} label="Total Comments" color="violet" />
          <StatCard icon={<Share size={20} />} value={loading ? '\u2014' : (summary['total_shares'] || 0).toLocaleString()} label="Total Shares" color="emerald" />
          <StatCard icon={<MousePointer size={20} />} value={loading ? '\u2014' : (summary['total_clicks'] || 0).toLocaleString()} label="Total Clicks" color="amber" />
        </div>

        {/* Content Overview Table */}
        <div>
          <h2 className="text-sm font-semibold text-neutral-800 mb-3">Content Overview</h2>

          <div className="mb-3">
            <DataTableToolbar
              search={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search content..."
              allColumns={ALL_COLUMNS}
              visibleColumns={prefs.visibleColumns}
              onToggleColumn={toggleColumn}
              density={prefs.density}
              onDensityChange={setDensity}
            />
          </div>

          {!loading && sortedItems.length === 0 ? (
            <Card>
              <EmptyState
                size="lg"
                variant="file"
                title="No content items yet"
                description="Create content items to start tracking performance"
              />
            </Card>
          ) : (
            <DataTable<ContentItem>
              data={sortedItems}
              columns={columns}
              sorting={prefs.sorting}
              onSortingChange={setSorting}
              columnVisibility={columnVisibility}
              globalFilter={search}
              density={prefs.density}
              isLoading={loading}
              getRowId={row => row.id}
              onRowClick={row => navigate(buildProjectUrl(`co/${row.id}`, 'sm'))}
            />
          )}

          {/* Expanded analytics rows - rendered outside DataTable as supplementary info */}
          {expandedItems.size > 0 && workspaceId && (
            <div className="mt-2 space-y-1">
              {sortedItems.filter(item => expandedItems.has(item.id)).map(item => (
                <Card key={item.id} padding="none">
                  <div className="px-4 py-1 text-xs font-medium text-neutral-600 bg-neutral-50 border-b border-neutral-100">
                    {item.title}
                  </div>
                  <table className="w-full">
                    <tbody>
                      <ItemAnalyticsRow workspaceId={workspaceId} itemId={item.id} />
                    </tbody>
                  </table>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Recent Distribution Activity */}
        <div>
          <h2 className="text-sm font-semibold text-neutral-800 mb-3">Recent Distribution Activity</h2>
          <Card padding="none">
            {loading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton variant="text" width={128} height={16} />
                    <Skeleton variant="text" width={80} height={16} />
                  </div>
                ))}
              </div>
            ) : recentActivity.length === 0 ? (
              <EmptyState
                size="md"
                icon={<Clock size={28} className="text-neutral-300" />}
                title="No distribution activity yet"
                description="Completed and failed distributions will appear here"
              />
            ) : (
              <div className="divide-y divide-neutral-100">
                {recentActivity.map(schedule => (
                  <div key={schedule.id} className="flex items-center gap-3 px-4 py-3">
                    {schedule.status === 'completed' ? (
                      <CheckCircle size={16} className="text-green-500 shrink-0" />
                    ) : (
                      <AlertCircle size={16} className="text-red-500 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-neutral-800 truncate">
                        {schedule.content_title || 'Untitled'}
                      </div>
                      <div className="text-xs text-neutral-400">
                        {SCHEDULE_TYPE_LABELS[schedule.schedule_type]}
                        {' · '}
                        {schedule.executed_at
                          ? new Date(schedule.executed_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                            })
                          : 'Not executed'}
                      </div>
                    </div>
                    <StatusPill
                      variant={schedule.status === 'completed' ? 'success' : 'error'}
                      label={SCHEDULE_STATUS_LABELS[schedule.status]}
                    />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Engagement notice */}
        {!hasAnyMetrics && !loading && (
          <Card padding="lg">
            <EmptyState
              size="lg"
              icon={<div className="w-16 h-16 bg-coral-100 rounded-2xl flex items-center justify-center"><BarChart3 className="w-8 h-8 text-coral-500" /></div>}
              title="Engagement Analytics"
              description="Analytics data is collected automatically every 6 hours from LinkedIn for all distributed posts. Distribute content via the Channels page to start collecting metrics."
              action={
                <div className="flex items-center justify-center gap-4 text-xs text-neutral-400">
                  <span className="flex items-center gap-1"><CheckCircle size={12} className="text-green-400" /> Content tracked</span>
                  <span className="flex items-center gap-1"><CheckCircle size={12} className="text-green-400" /> Distribution pipeline active</span>
                  <span className="flex items-center gap-1"><CheckCircle size={12} className="text-green-400" /> Analytics collection enabled</span>
                </div>
              }
            />
          </Card>
        )}
      </div>
    </DashboardPageLayout>
  );
};

export default AnalyticsPage;
